import React, { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import PageLoader from "../common/ui/PageLoader";
import { LocalStorageVariables } from "../common/entities/type";

type AuthRouteProps = {
  children: React.ReactNode;
};

/**
 * AuthRoute component - Redirects authenticated users away from auth pages
 * - If user is not logged in: Shows the auth page
 * - If a redirect URL is saved (user came from a protected page): Let the login flow handle it
 * - If user is logged in and NOT simple user (admin/affiliate): Redirects to /client/client-list
 * - If user is logged in and IS simple user (customer):
 *   - If biometrics_questionnaire_completed is undefined: Shows auth page (login flow in progress)
 *   - If biometrics_questionnaire_completed is false: Redirects to /intro
 *   - If biometrics_questionnaire_completed is true: Redirects to /platform/dashboard
 * - If mode=signup is in URL and user is logged in: Logs out and shows auth page
 */
const AuthRoute: React.FC<AuthRouteProps> = ({ children }) => {
  const { AuthUser, AuthIsLoading, AuthIsSimpleUser, AuthLogout } = useAuth();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const isSignupMode = mode === "signup";

  // Handle logout when mode=signup and user is already logged in.
  // Runs only on mount — reads localStorage directly (synchronous) instead of
  // AuthUser state (which is null on mount while AuthProvider hydrates).
  // Empty deps prevent re-running when AuthUser changes during registration.
  useEffect(() => {
    const existingUser = localStorage.getItem(LocalStorageVariables.USER);
    if (isSignupMode && existingUser) {
      AuthLogout({ redirect: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show loader while checking authentication status
  if (AuthIsLoading) {
    return <PageLoader />;
  }

  // If mode=signup, always show auth page (user was logged out above if needed)
  if (isSignupMode) {
    return <>{children}</>;
  }

  // If user is logged in, redirect to appropriate page based on role
  if (AuthUser !== null) {
    // If a redirect URL is pending, let the login handler in SignInForm manage
    // the redirect via window.location.href (ensures fresh page load with data)
    const pendingRedirect = localStorage.getItem(
      LocalStorageVariables.REDIRECT_URL
    );
    if (pendingRedirect) {
      return <>{children}</>;
    }

    // Admin/affiliate users (role 2-5) go to client list
    if (!AuthIsSimpleUser) {
      return <Navigate to="/client/client-list" replace />;
    }

    // For simple users (customers), check biometrics_questionnaire_completed
    // If undefined, the login flow is still in progress - don't redirect yet
    if (AuthUser.biometrics_questionnaire_completed === undefined) {
      return <>{children}</>;
    }

    // Customer users who haven't completed biometric questionnaire go to intro
    if (!AuthUser.biometrics_questionnaire_completed) {
      return <Navigate to="/intro" replace />;
    }

    // Customer users who completed biometric questionnaire go to dashboard
    return <Navigate to="/platform/dashboard" replace />;
  }

  // User is not logged in, show auth page
  return <>{children}</>;
};

export default AuthRoute;
